import { FC } from "react";
import clsx from "clsx";
import { IconIdCard } from "components/icons/IconIdCard";
import { IconPerson } from "components/icons/IconPerson";
import {
  EmployeeChangeLogEntry,
  LabeledDataChange,
  Modifier,
} from "types/Employee";
import { IconExchange } from "components/icons/IconExchange";
import { IconChevronDown } from "components/icons/IconChevronDown";
import { useLanguage } from "contexts/LanguageContext";
import IconArrowTransition from "components/icons/IconArrowTransition";
import { getFullname, translateLabel } from "./utils/helpers";
import { FormattedNumber } from "components/base/FormattedNumber";

interface Props {
  data: EmployeeChangeLogEntry;
  open: boolean;
  onToggleOpen: () => void;
}

export const EmployeeChangeLogItem: FC<Props> = ({
  data,
  open,
  onToggleOpen,
}) => {
  const isHr = data.modifiedBy.role === "HR";
  const approver = data.changes.find((c) => c.label === "approver");
  let isApproval = false;
  if (approver && approver.old && approver.new) {
    isApproval =
      Object.entries(approver.old).length > 0 &&
      Object.entries(approver.new).length > 0;
  }

  return (
    <div className="employee-change-log-item">
      <Header
        modifiedAt={data.modifiedAt}
        modifiedBy={getFullname(data.modifiedBy)}
        isHr={isHr}
        isApproval={isApproval}
        open={open}
        onToggleOpen={onToggleOpen}
      ></Header>
      <div className={clsx("collapsable", { open: open })}>
        <div className="item-changes">
          {data.changes.map((change) => (
            <Change
              key={change.label}
              change={change}
              currency={data.currency}
            ></Change>
          ))}
        </div>
      </div>
    </div>
  );
};

interface HeaderProps {
  modifiedAt: string;
  modifiedBy: string;
  isHr: boolean;
  isApproval: boolean;
  open: boolean;
  onToggleOpen: () => void;
}

const Header: FC<HeaderProps> = ({
  isApproval,
  isHr,
  modifiedAt,
  modifiedBy,
  open,
  onToggleOpen,
}) => {
  return (
    <button className="item-header" onClick={onToggleOpen}>
      <div className="type">
        <Icon isApproval={isApproval}></Icon>
        <IconExchange size={24}></IconExchange>
        <Icon isApproval={isApproval}></Icon>
      </div>
      <div className="date-and-user">
        <p className="date">{new Date(modifiedAt).toLocaleString()} </p>
        <p className="user">
          {modifiedBy}
          {isHr && <HrBadge></HrBadge>}
        </p>
      </div>
      <div className={clsx("open-indicator", { open: open })}>
        <IconChevronDown size={24}></IconChevronDown>
      </div>
    </button>
  );
};

const HrBadge = () => {
  return <span className="hr-badge">HR</span>;
};

interface IconProps {
  isApproval?: boolean;
}

const Icon: FC<IconProps> = ({ isApproval = false }) => {
  return (
    <div className={clsx("change-log-icon", { approval: isApproval })}>
      {isApproval ? (
        <IconPerson size={24}></IconPerson>
      ) : (
        <IconIdCard size={24}></IconIdCard>
      )}
    </div>
  );
};

const Change: FC<{ change: LabeledDataChange; currency?: string }> = ({
  change,
  currency,
}) => {
  const { messages, language } = useLanguage();

  if (!change.old && !change.new) {
    return null;
  }

  return (
    <div className="change">
      <div className="title">{translateLabel(change.label, messages)}</div>
      {change.label !== "reason" ? (
        <div className="values">
          <div>
            {
              <FormattedValue
                label={change.label}
                value={change.old}
                lang={language}
                messages={messages}
                currency={currency}
              ></FormattedValue>
            }
          </div>
          <div className="icon">
            <IconArrowTransition size={24}></IconArrowTransition>
          </div>
          <div>
            <FormattedValue
              label={change.label}
              value={change.new}
              lang={language}
              messages={messages}
              currency={currency}
            ></FormattedValue>
          </div>
        </div>
      ) : (
        <div>{change.new || change.old || "-"}</div>
      )}
    </div>
  );
};

interface FormattedValueProps {
  label: string;
  value: boolean | number | string | Modifier | undefined;
  messages: ReturnType<typeof useLanguage>["messages"];
  lang: string;
  currency?: string;
}

export const FormattedValue: FC<FormattedValueProps> = ({
  label,
  value,
  currency,
  lang,
  messages,
}) => {
  const currencies = ["fixum", "variable", "singlepayment", "companyCar"];
  const hours = ["hours"];
  const booleans = ["teamlead"];

  if (value === undefined || value === null) {
    return <span>{value}</span>;
  }
  if (currencies.includes(label)) {
    return (
      <span className="currency">
        <FormattedNumber
          value={value as number}
          type="currency"
          suffix={currency || "€"}
          lang={lang}
        ></FormattedNumber>
      </span>
    );
  }
  if (hours.includes(label)) {
    return (
      <span>
        ${value} ${messages.weekLabel}
      </span>
    );
  }
  if (booleans.includes(label)) {
    return value ? messages.information.yes : messages.information.no;
  }
  if (label === "approver") {
    return getFullname(value as Modifier);
  }
  if (label === "seniority") {
    return messages.seniorityLevel[String(value)];
  }
  if (label === "degree") {
    return messages.degreeLevels[String(value)];
  }
  return <span>{messages[String(value)] || String(value)}</span>;
};
